import { StatusCodes } from 'http-status-codes';
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useRecoilCallback } from 'recoil';
import { ApplicationRoutes, StaticRoutes } from '../../Router';
import { updateFundingDetails } from '../../api/vodaLend';
import { Header, Footer, ProductContainer, Loader } from '../../components';
import { Colors } from '../../constants';
import { useIsSuperMobile, productListFetch, fundingDetailsState, authDetail } from '../../recoil';
import styles from './ProductCatalogue.module.css';
import { useTealium } from '../../tealium/useTealium';
import { Carousel, CarouselItem } from '@vodacomsa/ns-react-components';
import { getMonthsInBusiness, extractNumber } from '../../helpers';
import { useLoader } from '../../components/Loader/useLoader';

const stylesRef = {
  productContainer: {
    display: 'flex',
    flexFlow: 'wrap',
    backgroundColor: Colors.GreyScale1,
    color: Colors.GreyScale13,
    justifyContent: 'flex-start'
  }
};

export const ProductCatalogue = () => {

  const navigate = useNavigate();
  const isSuperMobile = useRecoilValue(useIsSuperMobile);
  const [openLoader, closeLoader] = useLoader();
  const fetchedFundingDetails = useRecoilValue(fundingDetailsState);
  const authDetails = useRecoilValue(authDetail);
  const { track, events } = useTealium();
  const [fetchedProductList, setFetchedProductList] = useState(null);
  const isFundingDetailsValid = fetchedFundingDetails && Object.keys(fetchedFundingDetails).length > 0;

  useEffect(() => {
    track(events.productCatalogue.pageView());
  }, []);

  useEffect(() => {
    if (!isFundingDetailsValid) {
      navigate(StaticRoutes.FundingApplicationDetails);
    }
  }, [isFundingDetailsValid, navigate]);

  const productParams = {
    financeTenure: isFundingDetailsValid ? extractNumber(fetchedFundingDetails.financeTenure) : null,
    timeInBusiness: isFundingDetailsValid ? getMonthsInBusiness(fetchedFundingDetails.timeInBusiness) : null,
    revenueBracket: fetchedFundingDetails?.revenue,
    loanAmount: fetchedFundingDetails?.loanAmount
  };

  const fetchedProductListCallback = useRecoilCallback(({ snapshot }) => async () => {
    if (!isFundingDetailsValid) {
      return null;
    }

    const data = await snapshot.getPromise(productListFetch(productParams));
    return data;
  });

  useEffect(() => {
    const fetchProducts = async () => {
      const data = await fetchedProductListCallback();
      setFetchedProductList(data);
    };

    fetchProducts();
  }, [fetchedProductListCallback]);

  const [selectedProduct, setSelectedProduct] = useState(fetchedFundingDetails?.productId ? fetchedFundingDetails : {});
  const [isOpen, setIsOpen] = useState(false);

  const hasMatchedProducts = fetchedProductList?.matchingProducts?.length > 0;
  const hasNonMatchedProducts = fetchedProductList?.nonMatchingProducts?.length > 0;

  const toggleContainer = () => {
    setIsOpen(!isOpen);
  };

  const backClick = () => {
    track(events.productCatalogue.backClick());
    navigate(StaticRoutes.FundingApplicationDetails);
  };

  const updateSelectedProduct = useCallback((product) => {
    setSelectedProduct(product);
  }, []);

  const handleNext = async () => {
    openLoader();
    try {

      const productName = selectedProduct?.productName ?? '';
      await track(events.productCatalogue.nextClick(productName));
      const fundingDetailResponse = await updateFundingDetails(selectedProduct);

      if (fundingDetailResponse.status === StatusCodes.OK) {
        if (authDetails?.verification?.isVerified) {
          window.location.pathname = ApplicationRoutes.personalDetails;
        } else {
          window.location.pathname = ApplicationRoutes.identityVerification;
        }
      }
    } finally {
      closeLoader();
    }
  };

  const renderProducts = (products) => {

    if (!products?.length) {
      return null;
    }

    return isSuperMobile ? (
      <div className={styles.carouselContainer}>
        <Carousel dots className={styles.carouselCustom}>
          {products.map((product) => (
            <CarouselItem key={product.productId} className={styles.carouselItem}>
              <ProductContainer
                updateSelectedProduct={updateSelectedProduct}
                isSelected={selectedProduct.productId === product.productId}
                showProductCriteria={true}
                {...product}
              />
            </CarouselItem>
          ))}
        </Carousel>
      </div>
    ) : (
      <div style={stylesRef.productContainer}>
        {products.map((product) => (
          <ProductContainer
            key={product.productId}
            updateSelectedProduct={updateSelectedProduct}
            isSelected={selectedProduct.productId === product.productId}
            showProductCriteria={true}
            {...product}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <Header />
      {fetchedProductList ? (
        <div className={styles.mainContainer}>
          <h1 className={styles.productPageHead}>{'Choose a funding option for your business goals'}</h1>

          {renderProducts(hasMatchedProducts
            ? fetchedProductList?.matchingProducts
            : fetchedProductList?.nonMatchingProducts)}

          {hasMatchedProducts && hasNonMatchedProducts && (
            <div className={styles.collapsibleContainer}>
              <button
                type="button"
                className={styles.collapsibleHeader}
                onClick={toggleContainer}
                aria-expanded={isOpen}
                aria-controls="collapsibleContent">
                <h3>
                  Other business funding solutions <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
                </h3>
              </button>
              {isOpen && (
                <div
                  id="collapsibleContent"
                  className={`collapsibleContent ${isOpen ? 'open' : ''}`}>
                  {renderProducts(fetchedProductList?.nonMatchingProducts)}
                </div>
              )}
            </div>
          )}

          <Footer
            leftTitle={'Back'}
            rightTitle='Next'
            leftCallback={backClick}
            rightCallback={selectedProduct.productId ? () => handleNext() : null}
          />
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
};